import React, { useMemo, useRef, useState } from 'react';
import { TreeSelect } from '@duxton-web/ui';
import { TreeSelectProps } from '@duxton-web/ui/lib';
import { uniq } from 'lodash';

import { getCountriesTree, getKeyValuePairs } from '@/common/CountryCodesMap';
import {
  getSplittedSearchValues,
  getTokenizedMatchedValues,
  sortTreeDataByExactMatch,
} from '@/pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/ConstraintValueInput/helper';

interface OwnProps {
  ['data-testid']?: string;
}

const defaultProps = {
  showSearch: true,
  treeCheckable: true,
  popupMatchSelectWidth: false,
  placeholder: 'Select Country',
  filterTreeNode: true,
  treeNodeFilterProp: 'title',
  style: { width: '100%', minWidth: 200 },
};

export default function CountrySelector({
  'data-testid': dataTestId,
  ...props
}: OwnProps & TreeSelectProps) {
  const [searchValue, setSearchValue] = useState<string>('');

  const countriesTreeNode = getCountriesTree();
  const selectComponentRef = useRef<any>(null);

  function onSearch(searchValue: string) {
    setSearchValue(searchValue);
  }

  function parsePropsValue() {
    const value = props?.value;

    if (!value || value === '[]') {
      return [];
    }

    return value.startsWith('[') && props.value.endsWith(']')
      ? value.slice(1, -1).split(',')
      : [value.toString()];
  }

  const onValueChanged = (
    value: string[] | string,
    labelList: React.ReactNode[],
  ) => {
    const onChange = props.onChange;

    if (Array.isArray(value))
      onChange('[' + value.join(',') + ']', labelList, null);

    if (typeof value === 'string') onChange(value, labelList, null);
  };

  const handleListSelect = (e: { keyCode: number }) => {
    const isListInput =
      searchValue.includes(',') &&
      getSplittedSearchValues(searchValue).length > 1;

    if (e.keyCode === 13 && isListInput) {
      const keyValuePairs = getKeyValuePairs();
      const splittedSearchValues = getSplittedSearchValues(searchValue);
      const matchedValues = getTokenizedMatchedValues(
        splittedSearchValues,
        keyValuePairs,
        'value',
      );
      const matchedOptionValues = keyValuePairs
        .filter(({ value }) =>
          matchedValues.some(
            (item) => item.toLowerCase() === value.toLowerCase(),
          ),
        )
        .map(({ key }) => key);

      const currentValues = parsePropsValue();

      let newValues = [...currentValues, ...matchedOptionValues];
      newValues = uniq(newValues);

      onValueChanged(newValues, []);
      setSearchValue('');
      selectComponentRef.current.blur();
    }
  };

  const sortedTreeData = useMemo(
    () => sortTreeDataByExactMatch(countriesTreeNode ?? [], searchValue),
    [countriesTreeNode, searchValue],
  );

  return (
    <TreeSelect
      {...{
        ...defaultProps,
        treeData: sortedTreeData,
        value: parsePropsValue(),
        onChange: onValueChanged,
        treeCheckable: props?.multiple,
        ['data-testid']: dataTestId,
        onKeyDown: handleListSelect,
        onSearch: onSearch,
        ref: selectComponentRef,
        disabled: props?.disabled,
      }}
    />
  );
}
